import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import {buildContactDisplayName} from '../../src/helpers/models/contactHelpers';
import {RecipientRolesEnum} from '../../src/models/api/recipientModel';
import {VisualRecipientModel} from '../../src/models/api/visualRecipientModel';

/*
 * Props.
 */

interface RecipientsFormatterProps {
  /** The recipients to render. */
  recipients: ReadonlyArray<VisualRecipientModel>;
  /** The maximum number of names to display. */
  limit?: number;
}

/*
 * Component.
 */

export const RecipientsFormatter: FC<RecipientsFormatterProps> = ({recipients, limit}) => {
  const visibleRecipients = findVisibleRecipients(recipients);

  switch (visibleRecipients.length) {
    // If no recipients were provided, nothing to render.
    case 0:
      return null;

    // If we have only one recipient, render in full.
    case 1:
      return <>{buildContactDisplayName(visibleRecipients[0])}</>;

    // Otherwise, more complicated logic.
    default:
      return renderManyRecipients(visibleRecipients, limit);
  }
};

function findVisibleRecipients(recipients: ReadonlyArray<VisualRecipientModel>) {
  // If all recipients are BCC, no filtering needing.
  const hasOnlyBcc = recipients.every((r) => r.role === RecipientRolesEnum.BCC);
  if (hasOnlyBcc) {
    return recipients;
  }

  // Otherwise, exclude BCC because auto-bcc will appear on every conversation.
  return recipients.filter((r) => r.role !== RecipientRolesEnum.BCC);
}

function renderManyRecipients(recipients: ReadonlyArray<VisualRecipientModel>, limit = 2) {
  // If we're not above the limit, just render all the names.
  if (recipients.length <= limit) {
    const firstRecipientNames = renderRecipientNames(recipients.slice(0, -1));
    const lastRecipientName = renderRecipientNames(recipients.slice(-1));
    return (
      <Trans id="recipients-many">
        {firstRecipientNames} and {lastRecipientName}
      </Trans>
    );
  }

  // Otherwise, render some names and a count.
  const actualLimit = limit - 1;
  const invisibleRecipientCount = recipients.length - actualLimit;
  const visibleRecipients = recipients.slice(0, actualLimit);
  const visibleRecipientNames = renderRecipientNames(visibleRecipients);
  return (
    <Trans id="recipients-above-limit">
      {visibleRecipientNames} and {invisibleRecipientCount} others
    </Trans>
  );
}

function renderRecipientNames(recipients: ReadonlyArray<VisualRecipientModel>) {
  return recipients.map((t) => buildContactDisplayName(t)).join(', ');
}
